import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FileUpload } from '../ui/FileUpload';
import { ownerDetailsSchema } from '../../schemas/registrationSchemas';
import { usePanVerification } from '../../hooks/usePanVerification';
import type { OwnerDetails } from '../../types/registration';
import { Button } from '@/components/ui/button';
import { Input } from '../ui/Input';
import { useMutation, useQuery } from 'react-query';
import { processStep3, processStep3Fallback, verifyPan } from 'pages/AssistedPartnerRegistration/services/api';
import { convertFileToBase64 } from 'pages/CreateRemit/documentation';
import { toast } from 'sonner';
import WrappedAccordion from '../WrappedAccordion';
import { Spinner } from 'components';
import FormPopup from 'pages/CustomerRegistration/PANandAadhaarPopup/formpopup';
import PanAadhaarPopup from '../modal/fallbackStep3';

interface OwnerDetailsFormProps {
    onSubmit: (data: OwnerDetails) => void;
    onPrevious: () => void;
    initialData?: Partial<OwnerDetails>;
    onNext: () => void;
    currentStep?: number;
    defaultCompletedSteps?: Set<number>;
}

// Define accordion steps
enum AccordionStep {
    PAN_VERIFICATION = 3.1,
    AADHAAR_VERIFICATION = 3.2,
}

export const OwnerDetailsForm: React.FC<OwnerDetailsFormProps> = ({
    onSubmit,
    onPrevious,
    onNext,
    initialData,
    currentStep: defaultStep = AccordionStep.PAN_VERIFICATION,
    defaultCompletedSteps
}) => {
    const [panVerified, setPanVerified] = useState(false);
    const [aadhaarVerified, setAadhaarVerified] = useState(false);
    const [panDetails, setPanDetails] = useState<any>(null);
    const [aadhaarDetails, setAadhaarDetails] = useState<any>(null);
    const [panType, setPanType] = useState<'personal' | 'business'>('personal');
    const [isOpen, setIsOpen] = useState(false);
    
    // Accordion flow state
    const [currentStep, setCurrentStep] = useState<AccordionStep>(
        defaultStep as AccordionStep || AccordionStep.PAN_VERIFICATION
    );
    const [completedSteps, setCompletedSteps] = useState<Set<AccordionStep>>(
        defaultCompletedSteps || new Set()
    );

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        watch,
        formState: { errors, isValid },
    } = useForm<OwnerDetails>({
        resolver: zodResolver(ownerDetailsSchema),
        defaultValues: initialData,
        mode: 'onChange',
    });

    const panNumber = watch('panNumber');
    const aadhaarNumber = watch('aadhaarNumber');

    const { data: panVerificationData } = useQuery({
        queryKey: ['panDetails', panNumber],
        queryFn: async () => {
            return verifyPan({
                pan_no: panNumber,
            })
        },
        enabled: !!panNumber,
        onSuccess: (data) => {
            setPanType(data.typeOfHolder === 'Company' ? 'business' : 'personal');
            setPanDetails(data);
            setPanVerified(true);
        },
        onError: (error: any) => {
            setPanVerified(false);
        },
    });

    const { mutate, isLoading } = useMutation({
        mutationFn: processStep3,
        onSuccess: () => {
            if (currentStep === AccordionStep.PAN_VERIFICATION) {
                setCompletedSteps((prev) => new Set(prev).add(AccordionStep.PAN_VERIFICATION));
                setCurrentStep(AccordionStep.AADHAAR_VERIFICATION);
                toast.success('PAN verification successful!');
            } else if (currentStep === AccordionStep.AADHAAR_VERIFICATION) {
                setCompletedSteps((prev) => new Set(prev).add(AccordionStep.AADHAAR_VERIFICATION));
                toast.success('Owner details submitted successfully');
                onSubmit(getValues());
                onNext();
            }
        },
        onError: (error: any) => {
            if('Unable to locate var: channels_1.aadhar_front.path' === error?.response?.data?.message) {
                toast.error('Aadhaar verification failed.');
            }
            else toast.error(error?.response?.data?.message || `Failed to verify ${currentStep === AccordionStep.PAN_VERIFICATION ? 'PAN' : 'Aadhaar'}`);
            setIsOpen(true);
        }
    });

    const { mutateAsync: fallbackMutation, isLoading: fallbackIsLoading } = useMutation({
        mutationFn: processStep3Fallback,
        onSuccess: () => {

            if( currentStep === AccordionStep.PAN_VERIFICATION) {
                setCompletedSteps((prev) => new Set(prev).add(AccordionStep.PAN_VERIFICATION));
                setCurrentStep(AccordionStep.AADHAAR_VERIFICATION);
                toast.success('PAN verification successful!');
            }
            else if (currentStep === AccordionStep.AADHAAR_VERIFICATION) {
                setCompletedSteps((prev) => new Set(prev).add(AccordionStep.AADHAAR_VERIFICATION));
                onSubmit(getValues());
                onNext && onNext();
            }
            setIsOpen(false);
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || 'Failed to process step 3');
            setIsOpen(true);
        }
    });

    const handleFormSubmit = async (data: Partial<OwnerDetails>, step: AccordionStep) => {
        if (step === AccordionStep.PAN_VERIFICATION) {
            mutate({
                panNumber: data.panNumber || '',
                sub_step: step,
            });
        } else if (step === AccordionStep.AADHAAR_VERIFICATION) {
            mutate({
                aadhaarNumber: data.aadhaarNumber || '',
                panNumber: data.panNumber || '',
                sub_step: step,
            });
        }
    };

    ;

    const getAccordionTitle = (step: AccordionStep, isCompleted: boolean) => {
        const getTitleContent = () => {
            switch (step) {
                case AccordionStep.PAN_VERIFICATION:
                    return isCompleted ? (
                        <h3 className="w-full flex items-center justify-between text-lg pr-5">
                            Business Verification
                            <svg width="52" height="16" viewBox="0 0 52 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 0C3.592 0 0 3.592 0 8C0 12.408 3.592 16 8 16C12.408 16 16 12.408 16 8C16 3.592 12.408 0 8 0ZM11.824 6.16L7.288 10.696C7.176 10.808 7.024 10.872 6.864 10.872C6.704 10.872 6.552 10.808 6.44 10.696L4.176 8.432C3.944 8.2 3.944 7.816 4.176 7.584C4.408 7.352 4.792 7.352 5.024 7.584L6.864 9.424L10.976 5.312C11.208 5.08 11.592 5.08 11.824 5.312C12.056 5.544 12.056 5.92 11.824 6.16Z" fill="#0AA459" />
                                <path d="M25.7841 12H22.9588V3.27273H25.8736C26.7287 3.27273 27.4631 3.44744 28.0767 3.79688C28.6903 4.14347 29.1605 4.64205 29.4872 5.29261C29.8168 5.94034 29.9815 6.71733 29.9815 7.62358C29.9815 8.53267 29.8153 9.31392 29.483 9.96733C29.1534 10.6207 28.6761 11.1236 28.0511 11.4759C27.4261 11.8253 26.6705 12 25.7841 12ZM24.2756 10.8494H25.7116C26.3764 10.8494 26.929 10.7244 27.3693 10.4744C27.8097 10.2216 28.1392 9.85653 28.358 9.37926C28.5767 8.89915 28.6861 8.31392 28.6861 7.62358C28.6861 6.93892 28.5767 6.35795 28.358 5.88068C28.142 5.40341 27.8196 5.04119 27.3906 4.79403C26.9616 4.54687 26.429 4.4233 25.7926 4.4233H24.2756V10.8494ZM34.0338 12.1321C33.4202 12.1321 32.8846 11.9915 32.4273 11.7102C31.9699 11.429 31.6148 11.0355 31.3619 10.5298C31.1091 10.0241 30.9827 9.43324 30.9827 8.7571C30.9827 8.07812 31.1091 7.48437 31.3619 6.97585C31.6148 6.46733 31.9699 6.07244 32.4273 5.79119C32.8846 5.50994 33.4202 5.36932 34.0338 5.36932C34.6474 5.36932 35.1829 5.50994 35.6403 5.79119C36.0977 6.07244 36.4528 6.46733 36.7057 6.97585C36.9585 7.48437 37.0849 8.07812 37.0849 8.7571C37.0849 9.43324 36.9585 10.0241 36.7057 10.5298C36.4528 11.0355 36.0977 11.429 35.6403 11.7102C35.1829 11.9915 34.6474 12.1321 34.0338 12.1321ZM34.0381 11.0625C34.4358 11.0625 34.7653 10.9574 35.0267 10.7472C35.2881 10.5369 35.4812 10.2571 35.6062 9.90767C35.7341 9.55824 35.798 9.1733 35.798 8.75284C35.798 8.33523 35.7341 7.9517 35.6062 7.60227C35.4812 7.25 35.2881 6.96733 35.0267 6.75426C34.7653 6.54119 34.4358 6.43466 34.0381 6.43466C33.6375 6.43466 33.3051 6.54119 33.0409 6.75426C32.7795 6.96733 32.5849 7.25 32.4571 7.60227C32.3321 7.9517 32.2696 8.33523 32.2696 8.75284C32.2696 9.1733 32.3321 9.55824 32.4571 9.90767C32.5849 10.2571 32.7795 10.5369 33.0409 10.7472C33.3051 10.9574 33.6375 11.0625 34.0381 11.0625ZM39.5413 8.11364V12H38.2672V5.45455H39.4902V6.51989H39.5711C39.7217 6.1733 39.9575 5.89489 40.2785 5.68466C40.6024 5.47443 41.0101 5.36932 41.5015 5.36932C41.9476 5.36932 42.3382 5.46307 42.6734 5.65057C43.0086 5.83523 43.2686 6.1108 43.4532 6.47727C43.6379 6.84375 43.7302 7.29687 43.7302 7.83665V12H42.4561V7.99006C42.4561 7.51562 42.3325 7.14489 42.0853 6.87784C41.8382 6.60795 41.4987 6.47301 41.0669 6.47301C40.7714 6.47301 40.5086 6.53693 40.2785 6.66477C40.0513 6.79261 39.8709 6.98011 39.7373 7.22727C39.6066 7.47159 39.5413 7.76705 39.5413 8.11364ZM48.0201 12.1321C47.3752 12.1321 46.8198 11.9943 46.3539 11.7188C45.8908 11.4403 45.5328 11.0497 45.28 10.5469C45.03 10.0412 44.905 9.44886 44.905 8.76989C44.905 8.09943 45.03 7.50852 45.28 6.99716C45.5328 6.4858 45.8851 6.08665 46.3368 5.79972C46.7914 5.51278 47.3226 5.36932 47.9306 5.36932C48.2999 5.36932 48.6578 5.4304 49.0044 5.55256C49.351 5.67472 49.6621 5.86648 49.9377 6.12784C50.2132 6.3892 50.4306 6.72869 50.5897 7.14631C50.7488 7.56108 50.8283 8.06534 50.8283 8.65909V9.1108H45.6252V8.15625H49.5797C49.5797 7.82102 49.5115 7.52415 49.3752 7.26562C49.2388 7.00426 49.047 6.7983 48.7999 6.64773C48.5556 6.49716 48.2686 6.42188 47.9391 6.42188C47.5811 6.42188 47.2686 6.50994 47.0016 6.68608C46.7374 6.85937 46.5328 7.08665 46.388 7.3679C46.2459 7.64631 46.1749 7.94886 46.1749 8.27557V9.02131C46.1749 9.45881 46.2516 9.83097 46.405 10.1378C46.5613 10.4446 46.7786 10.679 47.057 10.8409C47.3354 11 47.6607 11.0795 48.0328 11.0795C48.2743 11.0795 48.4945 11.0455 48.6934 10.9773C48.8922 10.9062 49.0641 10.8011 49.209 10.6619C49.3539 10.5227 49.4647 10.3509 49.5414 10.1463L50.7473 10.3636C50.6507 10.7187 50.4774 11.0298 50.2274 11.2969C49.9803 11.5611 49.6692 11.767 49.2942 11.9148C48.922 12.0597 48.4973 12.1321 48.0201 12.1321Z" fill="#0AA459" />
                            </svg>
                        </h3>
                    ) : (
                        <h3 className="text-lg">PAN Verification</h3>
                    );
                case AccordionStep.AADHAAR_VERIFICATION:
                    return <h3 className="text-lg">Aadhaar Verification</h3>;
                default:
                    return <h3 className="text-lg">Step</h3>;
            }
        };

        return (
            <div className="flex items-center justify-between w-full">
                {getTitleContent()}
                {isCompleted && step !== AccordionStep.PAN_VERIFICATION && (
                    <svg
                        className="w-5 h-5 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                        />
                    </svg>
                )}
            </div>
        );
    };

    const handleFallbackSubmit = async (data: any) => {
        let dataToSend = {};
        if (currentStep === AccordionStep.PAN_VERIFICATION) {
            dataToSend = {
                pan_no: data.pan,
                name: data.name,
                dob: data.dob,
            };
        }
        if (currentStep === AccordionStep.AADHAAR_VERIFICATION) {
            dataToSend = {
                aadhar_no: data.aadhaar_number.toString(),
                name: data.name,
                dob: data.date_of_birth,
                address: data.address,
            }
        }
        const stepToSend = currentStep === AccordionStep.PAN_VERIFICATION ? AccordionStep.PAN_VERIFICATION : AccordionStep.AADHAAR_VERIFICATION;
        await fallbackMutation({
            ...dataToSend,
            sub_step: stepToSend,
        });
    }

    return (
        <div className="w-full mx-auto">
            {(isLoading || fallbackIsLoading) && <Spinner />}
            {/* <PanAadhaarPopup
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                onSubmit={handleFallbackSubmit}
                aadhaar={Number(watch('aadhaarNumber'))}
                pan={watch('panNumber')}
            /> */}
            <FormPopup
                defaultValue={AccordionStep.PAN_VERIFICATION === currentStep ? watch('panNumber') : watch('aadhaarNumber')}
                entityType="partner"
                onSubmit={handleFallbackSubmit}
                dialogOpen={isOpen}
                setDialogOpen={setIsOpen}
                type={AccordionStep.PAN_VERIFICATION === currentStep ? "PAN" : "aadhaar"}
            />
            <div className="grid grid-cols-3 gap-8 w-full">
                <div className="col-span-2 space-y-8">
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Owner Details</h2>
                    </div>

                    <form onSubmit={e => e.preventDefault()} className="space-y-8">
                        {/* PAN Verification */}
                        <WrappedAccordion
                            open={currentStep === AccordionStep.PAN_VERIFICATION}
                            title={getAccordionTitle(
                                AccordionStep.PAN_VERIFICATION,
                                completedSteps.has(AccordionStep.PAN_VERIFICATION)
                            )}
                            onToggle={() => {
                                if (currentStep !== AccordionStep.PAN_VERIFICATION) {
                                    setCurrentStep(AccordionStep.PAN_VERIFICATION);
                                }
                            }}
                        >
                            <div className="max-w-sm mt-4 rounded-lg">
                                <div className="space-y-4">
                                    <div>
                                        <Input
                                            {...register('panNumber')}
                                            label="Enter PAN"
                                            placeholder="Eg. ABCDE1234F"
                                            error={errors.panNumber?.message}
                                            isValid={panVerified}
                                        />
                                    </div>
                                    <Button                                    
                                        onClick={handleSubmit((data) => {
                                            handleFormSubmit({
                                                panNumber: data.panNumber,
                                            }, AccordionStep.PAN_VERIFICATION);
                                        })}
                                        type="button"
                                        disabled={!panNumber || isLoading}
                                        className="bg-blue-700 w-[100px] text-white-A700 rounded-md py-2.5 px-4"
                                    >
                                        {isLoading ? "Saving..." : "Next"}
                                    </Button>
                                </div>
                            </div>
                        </WrappedAccordion>

                        {/* Aadhaar Verification */}
                        <WrappedAccordion
                            open={currentStep === AccordionStep.AADHAAR_VERIFICATION}
                            title={getAccordionTitle(
                                AccordionStep.AADHAAR_VERIFICATION,
                                completedSteps.has(AccordionStep.AADHAAR_VERIFICATION)
                            )}
                            disabled={!completedSteps.has(AccordionStep.PAN_VERIFICATION)}
                            onToggle={() => {
                                if (
                                    completedSteps.has(AccordionStep.PAN_VERIFICATION) &&
                                    currentStep !== AccordionStep.AADHAAR_VERIFICATION
                                ) {
                                    setCurrentStep(AccordionStep.AADHAAR_VERIFICATION);
                                }
                            }}
                        >
                            <div className="mt-4 max-w-sm rounded-lg">
                                <div className="space-y-4">
                                    <div>
                                        <Input
                                            {...register('aadhaarNumber')}
                                            label="Aadhaar Number"
                                            placeholder="Eg. 123412341234"
                                            error={errors.aadhaarNumber?.message}
                                            isValid={aadhaarVerified}
                                        />
                                    </div>
                                    <Button
                                        onClick={handleSubmit((data) => {
                                            handleFormSubmit(data, AccordionStep.AADHAAR_VERIFICATION);
                                        })}
                                        type="button"
                                        disabled={!aadhaarNumber || isLoading}
                                        className="bg-blue-700 text-white-A700 w-[100px] rounded-md py-2.5 px-4"
                                    >
                                        {isLoading ? "Saving..." : "Next"}
                                    </Button>
                                </div>
                            </div>
                        </WrappedAccordion>
                    </form>
                </div>

                {/* Instructions Panel */}
                <div className="bg-gray-50 rounded-lg p-6 h-fit space-y-6 w-fit">
                    <div className="flex flex-col items-start gap-3">
                        <div className="bg-gray-50 border p-4 border-[#E4E4E4] rounded-lg h-fit max-w-xs">
                            <h4 className="font-medium text-gray-900 mb-4">Instructions</h4>
                            <div className="space-y-4 text-sm text-gray-600">
                                Upload personal or business card.
                                Payouts will be received in the bank account with the same PAN Card
                            </div>
                        </div>
                        {panVerified && panDetails && (
                            <div className="bg-white-A700 shadow-md space-y-2 p-3 rounded-lg w-full">
                                <p className="text-sm text-gray-600">
                                    The PAN <strong>{panNumber}</strong> belongs to
                                </p>
                                <div className="flex p-2 gap-2 flex-col bg-[#F2F7FF] rounded-lg">
                                    <div className="space-x-2 flex items-center gap-2">
                                        <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M14.6667 2.33402H11.3333V1.66733C11.3333 0.931984 10.7354 0.333984 10 0.333984H6C5.26466 0.333984 4.66666 0.931953 4.66666 1.66733V2.33398H1.33334C0.597969 2.33402 1.38604e-08 2.93198 1.38604e-08 3.66733V5.66733C1.38604e-08 6.4027 0.597969 7.00067 1.33334 7.00067H6.66669V6.66733C6.66666 6.62354 6.67526 6.58019 6.692 6.53973C6.70875 6.49927 6.7333 6.46251 6.76426 6.43155C6.79522 6.40059 6.83198 6.37604 6.87243 6.3593C6.91289 6.34256 6.95625 6.33396 7.00003 6.33398H9.00003C9.04381 6.33396 9.08717 6.34256 9.12763 6.3593C9.16809 6.37604 9.20485 6.40059 9.2358 6.43155C9.26676 6.46251 9.29132 6.49927 9.30806 6.53973C9.3248 6.58019 9.3334 6.62354 9.33337 6.66733V7.00067H14.6667C15.402 7.00067 16 6.4027 16 5.66733V3.66733C16 2.93198 15.402 2.33402 14.6667 2.33402ZM10 2.33402H6V1.66733H10V2.33402ZM15.8151 7.36036C15.7595 7.33281 15.6974 7.32129 15.6356 7.3271C15.5739 7.3329 15.5149 7.35581 15.4655 7.39323C15.2285 7.57261 14.9524 7.66733 14.6666 7.66733H9.33334V8.66733C9.33337 8.71111 9.32477 8.75447 9.30803 8.79493C9.29129 8.83538 9.26673 8.87214 9.23577 8.9031C9.20481 8.93406 9.16805 8.95862 9.1276 8.97536C9.08714 8.9921 9.04378 9.0007 9 9.00067H7C6.95622 9.0007 6.91286 8.9921 6.8724 8.97536C6.83194 8.95862 6.79519 8.93406 6.76423 8.9031C6.73327 8.87214 6.70871 8.83538 6.69197 8.79493C6.67523 8.75447 6.66663 8.71111 6.66666 8.66733V7.66733H1.33334C1.04753 7.66733 0.7715 7.57261 0.5345 7.39323C0.485173 7.35551 0.42623 7.33243 0.364406 7.32661C0.302582 7.3208 0.240369 7.3325 0.184875 7.36036C0.129331 7.38799 0.0825996 7.43057 0.0499382 7.48331C0.0172769 7.53605 -1.7873e-05 7.59686 1.38604e-08 7.65889L1.38604e-08 12.334C1.38604e-08 13.0694 0.597969 13.6674 1.33334 13.6674H14.6667C15.402 13.6674 16 13.0694 16 12.334V7.65889C16 7.59685 15.9827 7.53604 15.9501 7.4833C15.9174 7.43056 15.8706 7.38798 15.8151 7.36036Z" fill="#999999" />
                                        </svg>
                                        {panVerificationData?.name}
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-600">
                                            This is a {panVerificationData?.typeOfHolder === 'Company' ? 'Business' : 'Personal'} PAN
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {aadhaarVerified && aadhaarDetails && (
                        <div className="border-t pt-4">
                            <p className="text-sm text-gray-600 mb-2">
                                The Aadhaar <strong>{aadhaarNumber}</strong> belongs to
                            </p>
                            <div className="space-y-2">
                                <div className="flex items-center">
                                    <div className="w-5 h-5 bg-gray-300 rounded mr-2"></div>
                                    <p className="text-sm font-medium text-gray-900">{aadhaarDetails.name}</p>
                                </div>
                                <div className="flex items-start">
                                    <svg className="w-4 h-4 text-gray-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    </svg>
                                    <p className="text-sm text-gray-600">{aadhaarDetails.address}</p>
                                </div>
                            </div>

                            <div className="mt-4 pt-4 border-t">
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Instructions</h4>
                                <p className="text-sm text-gray-600">
                                    Please verify that the name on Aadhaar & PAN matches.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};